<template>
  <div>
    <b-alert v-if="unlinkedDocuments.length" show variant="warning" class="p-1 mb-1">
      <feather-icon icon="AlertTriangleIcon" class="mr-50" />
      <strong>Unlinked files.</strong> These client uploads are not attached to any requirement
      (their request was withdrawn or changed). Attach each to the requirement it belongs to.
      <b-row v-for="doc in unlinkedDocuments" :key="'unlinked' + doc.id" class="align-items-center mt-50 ml-1">
        <b-col cols="12" md="4" class="mb-25">
          <b-link @click="downloadDocument(doc.id, doc.FileName)">
            <feather-icon icon="DownloadIcon" size="12" class="mr-25" />{{ doc.FileName }}
          </b-link>
          <small v-if="doc.RequirementName" class="text-muted d-block">Uploaded as: {{ doc.RequirementName }}</small>
        </b-col>
        <b-col cols="12" md="5" class="mb-25">
          <v-select
            v-model="attachTargets[doc.id]"
            :options="requirements"
            :get-option-label="requirementLabel"
            placeholder="Attach to requirement…"
            :clearable="true"
          />
        </b-col>
        <b-col cols="12" md="3" class="mb-25">
          <b-button
            variant="warning"
            size="sm"
            :disabled="!attachTargets[doc.id] || busyId === doc.id"
            @click="reattach(doc)"
          >
            <b-spinner v-if="busyId === doc.id" small class="mr-25" />
            <feather-icon v-else icon="LinkIcon" size="14" class="mr-25" />
            Attach
          </b-button>
        </b-col>
      </b-row>
    </b-alert>

    <div class="d-flex justify-content-between align-items-center mb-1">
      <h5 class="mb-0">All files</h5>
      <b-button variant="primary" size="sm" :disabled="uploading" @click="openSharedUpload">
        <b-spinner v-if="uploading && !uploadTarget" small class="mr-25" />
        <feather-icon v-else icon="UploadIcon" size="14" class="mr-25" />
        Upload shared document
      </b-button>
    </div>

    <b-table
      :items="documents"
      :fields="documentFields"
      show-empty
      empty-text="No documents uploaded yet."
      responsive
      small
      class="mb-2"
    >
      <template #cell(FileName)="{ item }">
        <b-link @click="downloadDocument(item.id, item.FileName)">{{ item.FileName }}</b-link>
        <b-badge v-if="item.IsCurrent" variant="light-success" class="ml-25" title="The file currently attached to its requirement">
          Current
        </b-badge>
        <b-badge v-if="item.Unlinked" variant="light-warning" class="ml-25">Unlinked</b-badge>
      </template>
      <template #cell(For)="{ item }">
        <template v-if="item.CustomerRequestTitle">
          {{ item.CustomerRequestTitle }}
          <small class="text-muted d-block">Request from the client</small>
        </template>
        <template v-else-if="item.RequirementName">
          {{ item.RequirementName }}
          <small v-if="item.PersonLabel" class="text-muted d-block">{{ item.PersonLabel }}</small>
        </template>
        <span v-else class="text-muted">Shared file</span>
      </template>
      <template #cell(Note)="{ value }">
        <span v-if="value" class="font-italic">{{ value }}</span>
        <span v-else class="text-muted">—</span>
      </template>
      <template #cell(ReviewStatus)="{ item }">
        <b-badge :variant="reviewStatusVariant(item.ReviewStatus)">{{ reviewStatusLabel(item.ReviewStatus) }}</b-badge>
        <small v-if="item.ReviewStatus === 'Rejected' && item.ReviewReason" class="text-danger d-block">
          {{ item.ReviewReason }}
        </small>
      </template>
      <template #cell(Source)="{ item }">
        <b-badge :variant="sourceVariant(item.Source)">{{ item.Source }}</b-badge>
        <small class="text-muted d-block">{{ item.UploadedByName || "" }}</small>
      </template>
      <template #cell(FileSize)="{ value }">
        {{ formatSize(value) }}
      </template>
      <template #cell(created_at)="{ value }">
        {{ formatDateTime(value) }}
      </template>
      <template #cell(actions)="{ item }">
        <div class="text-nowrap">
          <b-button variant="flat-primary" size="sm" title="Download" @click="downloadDocument(item.id, item.FileName)">
            <feather-icon icon="DownloadIcon" size="14" />
          </b-button>
          <b-button
            variant="flat-danger"
            size="sm"
            title="Delete"
            :disabled="busyId === item.id"
            @click="deleteDocument(item)"
          >
            <feather-icon icon="Trash2Icon" size="14" />
          </b-button>
        </div>
      </template>
    </b-table>

    <h5>Requests from the client</h5>
    <p class="text-muted">
      Files the client asked SFC to provide (for example a signed copy of the agreement).
      Upload the file to fulfil the request, or decline it with a reason.
    </p>
    <b-table
      :items="customerRequests"
      :fields="requestFields"
      show-empty
      empty-text="The client has not requested any files."
      responsive
      small
    >
      <template #cell(Title)="{ item }">
        {{ item.Title }}
        <small v-if="item.Note" class="text-muted d-block">{{ item.Note }}</small>
      </template>
      <template #cell(Status)="{ item }">
        <b-badge :variant="requestStatusVariant(item.Status)">{{ item.Status }}</b-badge>
        <small v-if="item.Status === 'Declined' && item.DeclineReason" class="text-muted d-block">
          {{ item.DeclineReason }}
        </small>
        <template v-if="item.Status === 'Fulfilled' && item.FulfilledDocumentId">
          <b-link class="small d-block" @click="downloadDocument(item.FulfilledDocumentId, item.FulfilledFileName)">
            <feather-icon icon="DownloadIcon" size="12" class="mr-25" />{{ item.FulfilledFileName || "Download" }}
          </b-link>
          <small class="text-muted d-block">{{ formatDateTime(item.FulfilledAt) }}</small>
        </template>
      </template>
      <template #cell(created_at)="{ value }">
        {{ formatDateTime(value) }}
      </template>
      <template #cell(actions)="{ item }">
        <div v-if="item.Status === 'Requested'" class="text-nowrap">
          <b-button
            variant="flat-primary"
            size="sm"
            title="Upload the requested file"
            :disabled="busyId === 'req' + item.id"
            @click="openRequestUpload(item)"
          >
            <feather-icon icon="UploadIcon" size="14" class="mr-25" />Upload file
          </b-button>
          <b-button
            variant="flat-danger"
            size="sm"
            title="Decline this request"
            :disabled="busyId === 'req' + item.id"
            @click="openDecline(item)"
          >
            <feather-icon icon="XIcon" size="14" class="mr-25" />Decline
          </b-button>
        </div>
      </template>
    </b-table>

    <upload-modal
      v-model="uploadModal"
      :title="uploadTarget ? 'Fulfil the client\'s request' : 'Upload shared document'"
      :description="uploadTarget ? `The file is sent to the client as the answer to '${uploadTarget.Title}'.` : 'The file is not tied to a requirement; the client can see and download it.'"
      :busy="uploading"
      @upload="upload"
    />

    <review-modal
      v-model="declineModal"
      title="Decline Request"
      label="Reason (optional, shown to the client)"
      :description="declineTarget ? `Decline the request '${declineTarget.Title}'?` : ''"
      ok-title="Decline"
      ok-variant="danger"
      :reason-required="false"
      :busy="!!declineTarget && busyId === 'req' + declineTarget.id"
      @confirm="decline"
    />
  </div>
</template>

<script>
import vSelect from "vue-select";
import onboardingMixin from "./onboardingMixin";
import ReviewModal from "./ReviewModal.vue";
import UploadModal from "./UploadModal.vue";

export default {
  name: "OnboardingDocumentsTab",

  components: { vSelect, ReviewModal, UploadModal },

  mixins: [onboardingMixin],

  props: {
    onboardingId: { type: Number, required: true },
    documents: { type: Array, default: () => [] },
    customerRequests: { type: Array, default: () => [] },
    requirements: { type: Array, default: () => [] },
  },

  data() {
    return {
      busyId: null,
      attachTargets: {},
      uploadModal: false,
      uploadTarget: null,
      uploading: false,
      declineModal: false,
      declineTarget: null,
      documentFields: [
        { key: "FileName", label: "File" },
        { key: "For", label: "For" },
        { key: "Note", label: "Note" },
        { key: "ReviewStatus", label: "Review" },
        { key: "Source", label: "Source" },
        { key: "FileSize", label: "Size" },
        { key: "created_at", label: "Uploaded at" },
        { key: "actions", label: "", class: "text-right" },
      ],
      requestFields: [
        { key: "Title", label: "Request" },
        { key: "ContactName", label: "Requested by" },
        { key: "Status", label: "Status" },
        { key: "created_at", label: "Requested at" },
        { key: "actions", label: "", class: "text-right" },
      ],
    };
  },

  computed: {
    unlinkedDocuments() {
      return this.documents.filter((d) => d.Unlinked);
    },
  },

  methods: {
    requirementLabel(requirement) {
      let label = requirement.Name;
      if (requirement.Scope !== "Account") label += ` — ${requirement.PersonLabel || requirement.PersonName || requirement.Scope}`;
      if (requirement.Status) label += ` (${this.requirementStatusLabel(requirement.Status)})`;
      return label;
    },

    requestStatusVariant(status) {
      switch (status) {
        case "Requested":
          return "warning";
        case "Fulfilled":
          return "success";
        case "Declined":
          return "secondary";
        default:
          return "secondary";
      }
    },

    reattach(doc) {
      const requirement = this.attachTargets[doc.id];
      if (!requirement) return;
      this.busyId = doc.id;
      this.dispatchOnboarding("reattachDocument", { documentId: doc.id, requirementId: requirement.id })
        .then((res) => {
          this.toastSuccess((res.data && res.data.message) || "File attached.", "File Attached");
          this.$delete(this.attachTargets, doc.id);
          this.$emit("refresh");
        })
        .catch((err) => this.toastError(err, "Failed to attach the file."))
        .finally(() => {
          this.busyId = null;
        });
    },

    deleteDocument(item) {
      this.confirmBox(`Delete "${item.FileName}"? The client will no longer see it.`, {
        title: "Delete Document",
        okVariant: "danger",
        okTitle: "Delete",
      }).then((confirmed) => {
        if (!confirmed) return;
        this.busyId = item.id;
        this.dispatchOnboarding("deleteDocument", { documentId: item.id })
          .then(() => this.$emit("refresh"))
          .catch((err) => this.toastError(err, "Delete failed."))
          .finally(() => {
            this.busyId = null;
          });
      });
    },

    openSharedUpload() {
      this.uploadTarget = null;
      this.uploadModal = true;
    },

    openRequestUpload(request) {
      this.uploadTarget = request;
      this.uploadModal = true;
    },

    upload({ file, note }) {
      this.uploading = true;
      const formData = new FormData();
      formData.append("onboardingId", this.onboardingId);
      formData.append("file", file);
      if (this.uploadTarget) formData.append("customerRequestId", this.uploadTarget.id);
      if (note) formData.append("note", note);

      this.dispatchOnboarding("uploadInternalDocument", formData)
        .then((res) => {
          this.toastSuccess((res.data && res.data.message) || "Document uploaded.", "Uploaded");
          this.uploadModal = false;
          this.$emit("refresh");
        })
        .catch((err) => this.toastError(err, "Upload failed."))
        .finally(() => {
          this.uploading = false;
        });
    },

    openDecline(request) {
      this.declineTarget = request;
      this.declineModal = true;
    },

    decline(reason) {
      if (!this.declineTarget) return;
      this.busyId = "req" + this.declineTarget.id;
      this.dispatchOnboarding("declineCustomerRequest", {
        customerRequestId: this.declineTarget.id,
        reason: reason || null,
      })
        .then((res) => {
          this.toastSuccess((res.data && res.data.message) || "Request declined.", "Request Declined");
          this.declineModal = false;
          this.$emit("refresh");
        })
        .catch((err) => this.toastError(err, "Failed to decline the request."))
        .finally(() => {
          this.busyId = null;
        });
    },
  },
};
</script>
